// Calendar helpers — turn a Hub event into a downloadable .ics file so viewers
// can add it to Outlook / Google / Apple Calendar with one click.
//
// Events store a date (YYYY-MM-DD) plus start/end times as "HH:MM" strings with
// no timezone. We emit *floating* local times (no Z suffix, no TZID), which
// every calendar app interprets in the user's own timezone — the right behavior
// for an internal session that everyone joins at "their" 2pm.

export interface CalendarEvent {
  title: string;
  /** ISO date string, YYYY-MM-DD (the occurrence to add). */
  date: string;
  /** "HH:MM" 24-hour start time. */
  time_start: string;
  /** "HH:MM" 24-hour end time. */
  time_end: string;
  location?: string | null;
  note?: string | null;
}

/** "2026-06-26" + "14:00" -> "20260626T140000" (floating local time). */
function toICSStamp(date: string, time: string): string {
  const [y, m, d] = date.split("-");
  const [hh, mm] = time.split(":");
  return `${y}${m}${d}T${hh}${mm}00`;
}

/** Escape text per RFC 5545 (commas, semicolons, newlines, backslashes). */
function escapeICS(text: string): string {
  return text
    .replace(/\\/g, "\\\\")
    .replace(/\n/g, "\\n")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");
}

/** Build the .ics file contents for a single event occurrence. */
export function buildICS(event: CalendarEvent): string {
  const start = toICSStamp(event.date, event.time_start);
  const end = toICSStamp(event.date, event.time_end);
  // Deterministic UID from the event fields (no Date.now/random needed).
  const uid = `${start}-${event.title.replace(/\s+/g, "-")}@ai-builder-hub`;
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//AI Builder Hub//Events//EN",
    "CALSCALE:GREGORIAN",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTART:${start}`,
    `DTEND:${end}`,
    `SUMMARY:${escapeICS(event.title)}`,
  ];
  if (event.location) lines.push(`LOCATION:${escapeICS(event.location)}`);
  if (event.note) lines.push(`DESCRIPTION:${escapeICS(event.note)}`);
  lines.push("END:VEVENT", "END:VCALENDAR");
  return lines.join("\r\n");
}

/** Trigger a browser download of the event as an .ics file. */
export function downloadICS(event: CalendarEvent): void {
  const blob = new Blob([buildICS(event)], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${event.title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
